import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { MENU, entradaVisible } from '../../utils/menu';
import CazaErrores from '../common/CazaErrores';
import AvisoVersion from '../common/AvisoVersion';
import { AvisoCaducidadAlEntrar } from '../common/CaducidadAutorizaciones';
import { ENTREGA_PANTALLA } from '../../version';
import { useColoresEstado } from '../../context/ColoresEstadoContext';

// Mapa nombre → componente de icono. El menú (utils/menu.js) guarda solo el
// nombre para que sea un fichero de datos sin dependencias de React.
const ICONOS = {
  home: IconHome, bus: IconBus, doc: IconDoc, cal: IconCal, grid: IconGrid,
  building: IconBuilding, truck: IconTruck, users: IconUsers, hardhat: IconHardHat,
  list: IconList, plus: IconPlus, bell: IconBell, user: IconUser,
  shield: IconShield, settings: IconSettings, palette: IconPalette,
};

export default function AppLayout() {
  const { user, logout, puede } = useAuth();
  // Hasta que no estén los colores de los estados no se monta la pantalla
  // (entrega 278): si se montara antes, pintaría con los de siempre y
  // cambiaría de golpe medio segundo después. `version` sube cada vez que
  // se recargan (al guardar en Colores de estados) y va de `key`: cambiar la
  // clave VUELVE A MONTAR la pantalla, que es la única forma segura de que
  // hasta los trozos memorizados cojan los colores nuevos.
  const { listo: coloresListos, version: versionColores } = useColoresEstado();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  async function handleLogout() { await logout(); navigate('/login'); }

  return (
    <div style={{ display:'flex' }}>

      {/* Topbar móvil */}
      <div className="topbar">
        <button className="topbar-hamburger" onClick={() => setSidebarOpen(o=>!o)}>
          <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <line x1="3" y1="6"  x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div className="topbar-logo">Autocares RG</div>
        <NavLink to="/perfil" style={{ width:32, height:32, borderRadius:'50%',
          background:'rgba(255,255,255,.2)', display:'flex', alignItems:'center',
          justifyContent:'center', border:'1px solid rgba(255,255,255,.3)',
          color:'#fff', fontWeight:900, fontSize:11, textDecoration:'none' }}>
          {user?.username?.slice(0,2).toUpperCase()}
        </NavLink>
      </div>

      {/* Overlay */}
      <div className={`sidebar-overlay${sidebarOpen ? ' visible':''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' open':''}`}>
        <div className="sidebar-logo">
          <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,.2)',
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
            border:'1px solid rgba(255,255,255,.3)' }}>
            <span style={{ color:'#fff', fontWeight:900, fontSize:13 }}>RG</span>
          </div>
          <div className="sidebar-logo-text">Autocares<br/>Rodríguez Gómez</div>
        </div>

        <nav className="sidebar-nav">
          {MENU.map(grupo => {
            const visibles = grupo.items.filter(i => entradaVisible(i, puede));
            if (!visibles.length) return null;
            return (
              <div key={grupo.grupo}>
                <div className="nav-group-label">{grupo.grupo}</div>
                {visibles.map(item => {
                  // Estado activo teniendo en cuenta el ?tipo= para las agendas
                  const [itemPath, itemQuery] = item.to.split('?');
                  const itemTipo = new URLSearchParams(itemQuery || '').get('tipo') || '';
                  const curTipo  = new URLSearchParams(location.search).get('tipo') || '';
                  const activo = location.pathname === itemPath && curTipo === itemTipo;
                  const Icono = ICONOS[item.icono] || IconGrid;
                  return (
                    <NavLink key={item.to} to={item.to}
                      className={`nav-item${activo ? ' active' : ''}`}>
                      <Icono /><span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <NavLink to="/perfil" style={{ display:'flex', alignItems:'center', gap:10,
            color:'rgba(255,255,255,.85)', textDecoration:'none', marginBottom:10 }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,.2)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight:800, fontSize:12, flexShrink:0 }}>
              {user?.username?.slice(0,2).toUpperCase()}
            </div>
            <div className="user-meta">
              <div style={{ fontWeight:600, fontSize:13 }}>{user?.username}</div>
              <div style={{ fontSize:10, opacity:.7, textTransform:'uppercase' }}>{user?.rol_nombre || user?.rol}</div>
            </div>
          </NavLink>
          <button onClick={handleLogout} style={{ background:'none', border:'none',
            color:'rgba(255,255,255,.6)', cursor:'pointer', fontSize:13,
            display:'flex', alignItems:'center', gap:6, padding:0 }}>
            <IconLogout /> <span className="logout-text">Cerrar sesión</span>
          </button>
          {/* La entrega que está sirviendo el navegador. Pequeño y en gris:
              no estorba, y cuando haya que preguntar "¿qué versión tienes?"
              está aquí en vez de haber que adivinarlo. */}
          <div className="user-meta" style={{ marginTop:8, fontSize:10,
            color:'rgba(255,255,255,.35)', letterSpacing:.3 }}>
            entrega {ENTREGA_PANTALLA}
          </div>
        </div>
      </aside>

      {/* El CazaErrores va POR DENTRO del menú, no por fuera: si una pantalla
          se cae, se cae ella sola y el menú sigue ahí para irse a otro sitio.
          La `clave` es la ruta: al cambiar de pantalla se rearma solo. */}
      <main className="main-content">
        {/* El aviso de "a medio instalar" va FUERA del CazaErrores: si la
            pantalla se cae, el aviso tiene que seguir viéndose, porque muchas
            veces es la explicación de por qué se ha caído. */}
        <AvisoVersion />
        {/* Autorizaciones de ruta caducadas: una ventana al entrar, una vez
            por sesión, a quien tenga el permiso (entrega 300). */}
        <AvisoCaducidadAlEntrar />
        <CazaErrores clave={location.pathname + location.search}>
          {coloresListos
            ? <React.Fragment key={versionColores}><Outlet /></React.Fragment>
            : <div className="spinner-center"><div className="spinner" /></div>}
        </CazaErrores>
      </main>
    </div>
  );
}

// Iconos
function IconHome()     { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M3 12L12 3l9 9"/><path d="M9 21V12h6v9"/></svg>; }
function IconBus()      { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="14" rx="2"/><path d="M3 10h18M8 17v2m8-2v2"/><circle cx="8" cy="17" r="1"/><circle cx="16" cy="17" r="1"/></svg>; }
function IconDoc()      { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>; }
function IconCal()      { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>; }
function IconGrid()     { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>; }
function IconBuilding() { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="15" rx="1"/><path d="M16 22V12H8v10M12 7V2"/></svg>; }
function IconTruck()    { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M1 3h15v13H1zM16 8h4l3 4v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>; }
function IconUsers()    { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>; }
function IconHardHat()  { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M2 18h20M12 2a8 8 0 018 8H4a8 8 0 018-8z"/><path d="M4 10v4a2 2 0 002 2h12a2 2 0 002-2v-4"/></svg>; }
function IconList()     { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>; }
function IconPlus()     { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>; }
function IconBell()     { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/></svg>; }
function IconUser()     { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>; }
function IconSettings() { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>; }
function IconPalette()  { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 2a10 10 0 100 20 2 2 0 002-2v-1a2 2 0 012-2h2a4 4 0 004-4A10 10 0 0012 2z"/><circle cx="7.5" cy="11.5" r="1.2"/><circle cx="11" cy="7" r="1.2"/><circle cx="16.5" cy="8.5" r="1.2"/></svg>; }
function IconShield()   { return <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>; }
function IconLogout()   { return <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>; }
